import 'deep-chat';
import {Signals} from 'deep-chat/dist/types/handler';
import {RequestDetails} from "deep-chat/dist/types/interceptors";
import {DeepChat} from 'deep-chat';
import chatConfig from "./config/chat.config";
import appConfig from "./config/app.config";
import aiBotImg from "./assets/woosmap.svg"
import userImg from "./assets/user.svg"
import refreshImg from "bundle-text:./assets/refresh.svg"
import "styles/main.scss"
import WoosmapMapElement from "./components/mapComponent";
import {GeolocationService} from "./utils/geolocation";
import ToolUseComponent from "./components/toolUseComponent";
import ScrollToBottomComponent from "./components/scrollToBottomComponent";

import collapsibleStyles from 'bundle-text:./styles/components/collapsible.scss';
import loadingStyles from 'bundle-text:./styles/components/loading.scss';
import scrollToBottomStyles from 'bundle-text:./styles/components/scroll_to_bottom.scss';
import clearButtonStyles from 'bundle-text:./styles/components/clear_button.scss';
import chatbotStyles from 'bundle-text:./styles/components/chatbot.scss';


interface MessageContent {
    text?: string;
    toolUse?: any;
    toolResult?: any;
}

interface Message {
    role: string;
    content: MessageContent[];
}

interface OriginalRequest {
    body?: {
        messages?: Message[];
    };
}

export default class LocationChatBot {
    target: HTMLElement;
    chatElement!: DeepChat;
    historyMessages: Message[] = [];
    clearButton!: HTMLButtonElement;
    isFrench: boolean = false;
    mapRenderedIds: string[] = []
    toolUseComponent!: ToolUseComponent;
    collapsibleElements: Set<HTMLElement> = new Set();


    constructor({target}: { target: HTMLElement }) {
        this.target = target;
        this.init();
        customElements.define('woosmap-map', WoosmapMapElement);
    }

    init() {
        this.render();
    }

    render() {
        this.target.innerHTML = `
      <div class="container">
        <div class="wrapper">
        <h1>${appConfig.labels.title}</h1>
        <span class="divider"></span>
        <deep-chat></deep-chat>
        </div>
      </div>
    `;
        this.chatElement = this.target.querySelector('deep-chat') as DeepChat;
        this.configureChatElement();

    }


    getBrowserLanguage() {
        return navigator.language || navigator.userLanguage;
    }


    configureChatElement() {
        const browserLanguage = this.getBrowserLanguage();
        this.isFrench = browserLanguage.startsWith('fr');


        this.chatElement.avatars = {
            ai: {src: aiBotImg},
            user: {src: userImg},
            default: {styles: {position: 'left', avatar: {width: '2em', height: '2em'}}},
        };
        if (this.isFrench) {
            chatConfig.textInput.placeholder.text = "Bienvenue!"
        }
        this.chatElement.textInput = chatConfig.textInput;
        this.chatElement.introMessage = this.isFrench ? chatConfig.frenchIntroMessage : chatConfig.introMessage;
        this.chatElement.messageStyles = chatConfig.messageStyles;
        this.chatElement.auxiliaryStyle = chatConfig.auxiliaryStyle;
        this.chatElement.setAttribute("style", chatConfig.elementStyle);
        this.chatElement.displayLoadingBubble = false;
        this.chatElement.history = [];
        this.chatElement.speechToText = chatConfig.speechToText;
        this.chatElement.submitButtonStyles = chatConfig.submitButtonStyles;
        this.chatElement.remarkable = chatConfig.remarkable;
        this.handleChatInterceptors();
        //this.handleHTML5Position();
        window.setTimeout(() => {
            this.injectClearButton();
            this.injectCustomStyles();
            this.manageScrollToBottom();
        }, 100)
    }

    async handleHTML5Position() {
        const position: GeolocationPosition = await GeolocationService.getCurrentPosition();
        if (position) {
            const strPosition = JSON.stringify({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
            })
            const labelPosition = this.isFrench ? `Voici ma position ${strPosition}` : `Here is my location ${strPosition}`
            this.chatElement.submitUserMessage({
                text: labelPosition
            });
        }
    }

    clearChat() {
        this.historyMessages = [];
        const messagesContainer = this.chatElement?.shadowRoot?.querySelector("#messages");
        if (messagesContainer) {
            const children = Array.from(messagesContainer.children);
            children.forEach(child => {
                if (!child.classList.contains("deep-chat-intro")) {
                    messagesContainer.removeChild(child);
                }
            });
        }
        if (this.clearButton) {
            this.clearButton?.setAttribute("disabled", "true");
        }
    }

    manageScrollToBottom() {
        const messagesContainer = this.chatElement?.shadowRoot?.querySelector("#messages") as HTMLElement;
        if (messagesContainer) {
            new ScrollToBottomComponent(messagesContainer);
        }
    }

    injectCustomStyles() {
        const style = document.createElement('style');
        style.textContent = `${chatbotStyles}${collapsibleStyles}${loadingStyles}${scrollToBottomStyles}${clearButtonStyles}`
        const shadowRoot = this.chatElement?.shadowRoot
        shadowRoot?.appendChild(style);
    }

    injectClearButton() {
        this.clearButton = document.createElement('button');
        this.clearButton.innerHTML = refreshImg;
        this.clearButton.addEventListener("click", this.clearChat.bind(this));
        this.clearButton.setAttribute("disabled", "true")
        const shadowRoot = this.chatElement?.shadowRoot
        const targetElement = shadowRoot?.querySelector("#text-input-container")
        targetElement?.prepend(this.clearButton)
    }


    handleChatInterceptors(): void {
        if (!this.chatElement) {
            console.error('Chat element is not initialized.');
            return;
        }
        this.chatElement.connect = {
            websocket: true,
            handler: (_, signals) => {
                let text = '';
                let overwrite = false;
                let mapComponent: string | null = null;
                let toolUseHtml = ''

                try {
                    const websocket = new WebSocket(chatConfig.useWoosmapWSSUrl);
                    websocket.onopen = () => {
                        signals.onOpen(); // enables the user to send messages
                    };
                    websocket.onmessage = (message) => {
                        overwrite = !!text;
                        const response = JSON.parse(message.data);
                        if (response.stop_reason && response.stop_reason === 'end_turn') {
                            text = '';
                            // Display the stored map component if available
                            if (mapComponent) {
                                const mapMsg = this.createMapMessage(mapComponent);
                                if (mapMsg.role === 'woosmap') {
                                    this.chatElement.addMessage(mapMsg, false);
                                }
                                mapComponent = null; // Reset the map component variable
                            }
                        } else if (response.tool_use) {
                            // Create and display the ToolUseComponent as standard HTML
                            const toolUseContainer = document.createElement('div');
                            toolUseContainer.setAttribute('id', `tool-use-${response.tool_use}`);
                            this.toolUseComponent = new ToolUseComponent(toolUseContainer, response.tool_use, "loading");

                            // Convert the ToolUseComponent to a string representation
                            toolUseHtml = toolUseContainer.outerHTML;
                            text += toolUseHtml;
                            signals.onResponse({text, overwrite});
                        } else if (response.toolResult) {
                            const toolResultContent = response.toolResult.content.find((content: any) => content.json?.result);
                            if (toolResultContent) {
                                const result = toolResultContent.json.result;
                                const toolName = response.tool_use_block.name;

                                // Update the ToolUseComponent with the result

                                const toolUseContainer = document.createElement('div');
                                toolUseContainer.setAttribute('id', `tool-use-${toolName}`);
                                this.toolUseComponent = new ToolUseComponent(toolUseContainer, toolName, "success", response.tool_use_block);

                                // Replace the last occurrence of the component in the text
                                const toolUseSuccessHtml = toolUseContainer.outerHTML
                                text = text.replace(toolUseHtml, toolUseSuccessHtml);
                                if (toolName == "create_map") {
                                    mapComponent = result;
                                }
                                signals.onResponse({text, overwrite});
                            } else if (response.toolResult.error) {
                                // Update the ToolUseComponent with the error
                                const toolName = response.tool_use_block.name;
                                if (toolName == "create_map") {
                                    mapComponent = null;
                                }
                                const toolUseContainer = document.createElement('div');
                                toolUseContainer.setAttribute('id', `tool-use-${toolName}`);
                                this.toolUseComponent = new ToolUseComponent(toolUseContainer, toolName, "error", response.tool_use_block);

                                // Replace the last occurrence of the component in the text
                                const toolUseErrorHtml = toolUseContainer.outerHTML
                                text = text.replace(toolUseHtml, toolUseErrorHtml);
                                text += "<!-- end-tool-use -->"
                                signals.onResponse({text, overwrite});
                            }
                        } else if (response.text) {
                            text += response.text;
                            signals.onResponse({text, overwrite});
                        } else if (response.end_stream) {
                            this.historyMessages = response.end_stream
                            this.addCollapsibleListeners();
                        }
                        if (this.historyMessages.length > 0) {
                            this.clearButton.removeAttribute("disabled");
                        }
                    };
                    websocket.onclose = () => {
                        signals.onClose(); // stops the user from sending messages
                    };
                    websocket.onerror = () => {
                        // 'Connection error' is a special string that will also display in Deep Chat
                        signals.onResponse({error: 'Connection error'});
                    };
                    // triggered when the user sends a message
                    signals.newUserMessage.listener = (body) => {
                        // Ensure the message includes the action field
                        text = '';
                        toolUseHtml = '';
                        const message = {
                            action: "sendmessage",
                            ...body
                        };
                        websocket.send(JSON.stringify(message));
                    };
                } catch (e) {
                    signals.onResponse({error: 'error'}); // displays an error message
                    signals.onClose(); // stops the user from sending messages
                }
            },
        };
        this.chatElement.requestInterceptor = (originalRequest: RequestDetails) => {
            try {
                const messages = originalRequest.body?.messages;
                if (!messages || messages.length === 0) {
                    console.error('No messages found in the request body.');
                    return originalRequest;
                }
                const lastUserMessage = {
                    role: messages[0].role,
                    content: [
                        {
                            text: messages[0].text
                        }
                    ]
                };
                this.historyMessages.push(lastUserMessage)
                originalRequest.body = {messages: this.historyMessages};
                return originalRequest;
            } catch (error) {
                console.error('Error intercepting request:', error);
                return originalRequest;
            }
        };
    }

    createMapMessage(mapComponent: string) {
        if (mapComponent) {
            // Extract the map ID from the mapComponent string
            const mapIdMatch = mapComponent.match(/id="([^"]+)"/);
            const mapId = mapIdMatch ? mapIdMatch[1] : null;

            if (mapId && !this.mapRenderedIds.includes(mapId)) {
                this.mapRenderedIds.push(mapId);
                return {
                    html: mapComponent,
                    role: 'woosmap'
                };
            } else {
                return {error: 'Map already rendered - skipping', role: 'ai'};
            }
        } else {
            return {error: 'Error creating map message', role: 'ai'};
        }
    }

    addCollapsibleListeners() {
        this.chatElement?.shadowRoot?.querySelectorAll(".collapsible").forEach((element) => {
            if (!this.collapsibleElements.has(element as HTMLElement)) {
                const child = element.firstElementChild as HTMLElement;
                if (child) {
                    child.classList.add("collapsible-icon");
                }
                element.addEventListener("click", (e) => {
                    const target = e.currentTarget as HTMLElement;
                    this.toggleActive(target);
                });
                this.collapsibleElements.add(element as HTMLElement);
            }
        });
    }

    toggleActive(container: HTMLElement) {
        container.classList.toggle("active");
        const content = container.nextElementSibling as HTMLElement;
        if (content?.style.maxHeight && content?.style.maxHeight != "0px") {
            content.style.maxHeight = "0px";
        } else {
            content.style.maxHeight = content.scrollHeight + "px";
        }
    }

}